"use client"

import { toImageSrc } from "@/lib/utils"
import Image from "next/image"
import { useCallback, useEffect, useState } from "react"
import type { Swiper as SwiperInstance } from "swiper"
import { Swiper, SwiperSlide } from "swiper/react"

import "swiper/css"

import { PromotionItem } from "@/(admin)/modules/ui-builder/config/themes/tamda-one/PromotionShowcase/components/PromotionItem"
import type { PromotionShowcaseProps } from "@/(admin)/modules/ui-builder/config/themes/tamda-one/PromotionShowcase/types/config"

const styles = `
  /* Scoped overrides to neutralize global .swiper-slide rules (e.g. width:100%)
     and let Swiper compute slide sizes correctly so spaceBetween works. */
  .promotion-showcase-swiper .swiper-slide {
    padding: 0 !important;
    margin: 0 !important;
    box-sizing: border-box !important;
    width: auto !important;
    max-width: none !important;
    -ms-flex: 0 0 auto !important;
    flex: 0 0 auto !important;
  }

  /* Use wrapper gap to guarantee a consistent 20px between slides.
     We set Swiper spaceBetween=0 to avoid double spacing. */
  .promotion-showcase-swiper .swiper-wrapper {
    gap: 20px !important;
    display: flex !important;
    align-items: stretch !important;
  }
`

interface PromotionShowcaseContentProps
  extends Omit<PromotionShowcaseProps, "styles" | "responsiveType" | "animation"> {
  id: string
}

export const PromotionShowcaseContent: React.FC<PromotionShowcaseContentProps> = ({
  id,
  sectionTitle,
  logo,
  promotions,
  slidesPerView = 5,
  spaceBetween = 0,
  className,
}) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const [swiperInstance, setSwiperInstance] = useState<SwiperInstance | null>(null)

  // Default state (nothing hovered) mirrors the first promotion — title & background.
  const currentPromotion =
    (activeIndex !== null ? promotions[activeIndex] : undefined) ?? promotions?.[0]

  const currentTitle = currentPromotion?.title ?? ""

  const outsideBackgroundUrl = currentPromotion?.outsideBackgroundImage
    ? toImageSrc(currentPromotion.outsideBackgroundImage)
    : null

  const logoUrl = toImageSrc(logo)

  const handleServiceClick = useCallback(
    (index: number) => {
      setActiveIndex(index)
      if (swiperInstance) {
        swiperInstance.slideToLoop(index, 400)
      }
    },
    [swiperInstance],
  )

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest(".service-item-container")) {
        setActiveIndex(null)
      }
    }

    document.addEventListener("click", handleClickOutside)
    return () => document.removeEventListener("click", handleClickOutside)
  }, [])

  return (
    <div className={`relative flex items-center sm:min-h-[682px] sm:max-h-[682px] overflow-hidden ${className}`}>
      <style>{styles}</style>
      <div className="absolute inset-0">
        {outsideBackgroundUrl && (
          <Image
            alt={currentTitle}
            src={outsideBackgroundUrl}
            fill
            sizes="100vw"
            className="object-cover w-full h-full"
            key={outsideBackgroundUrl}
            priority
          />
        )}
      </div>

      <div className="absolute inset-0 bg-black opacity-70" />

      <div className="container sm:h-full relative z-10 mx-auto items-center flex justify-center">
        <div className="flex sm:flex-row sm:h-full flex-col sm:gap-[30px] w-full  gap-[22px] max-w-[1290px] px-[15px] items-center sm:p-0 pr-0 pb-[61px] pt-4">
          <div className="relative w-full sm:w-[32.5%]">
            {logo && (
              <div className="sm:block hidden w-[133px] h-[48px]">
                <Image
                  src={logoUrl}
                  alt={sectionTitle}
                  width={133}
                  height={48}
                  className="object-cover w-full h-full"
                />
              </div>
            )}

            {/* Section title for small screens only */}
            {sectionTitle && (
              <h2 className="sm:hidden block text-base leading-6 font-black text-white uppercase whitespace-normal break-words mb-3">
                {sectionTitle}
              </h2>
            )}

            <h2 className="sm:text-[70px] sm:leading-[68px] text-[40px] leading-[40px] font-black text-white whitespace-normal break-words pt-4 pb-[15px] max-w-full w-full">
              {currentTitle}
            </h2>
          </div>

          <div className="w-full  sm:w-[67.5%]">
            <div className="service-item-container w-full sm:!w-[1080px] flex flex-col sm:gap-[68px] gap-[25px]">
              <h2 className="hidden sm:block sm:text-xl sm:leading-[26px] text-lg leading-[25.2px] font-black text-white uppercase">
                {sectionTitle}
              </h2>
              <div>
                <Swiper
                  className="promotion-showcase-swiper"
                  spaceBetween={0}
                  slidesPerView={"auto"}
                  loop={true}
                  grabCursor={true}
                  onSwiper={s => {
                    setSwiperInstance(s)
                  }}
                  breakpoints={{
                    480: { spaceBetween: 0 },
                    640: { spaceBetween: 0 },
                    768: { spaceBetween: 0 },
                    1024: { spaceBetween: 0 },
                    1280: { spaceBetween: 0 },
                  }}
                >
                  {promotions.map((promotion, index) => (
                    <SwiperSlide key={index}>
                      <PromotionItem
                        promotion={promotion}
                        isActive={activeIndex === index}
                        onClick={() => handleServiceClick(index)}
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
